import { useEffect, useMemo, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import SearchableSelect from "../../components/SearchableSelect";
import DriveExplorer, { ViewToggle, BulkActionBar, formatBytes } from "../../components/DriveExplorer";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../contexts/AuthContext";
import {
  listOrgFiles,
  uploadOrgFiles,
  deleteOrgFile,
  renameOrgFile,
  updateOrgFile,
  createOrgFolder,
  updateOrgFolder,
  deleteOrgFolder,
  downloadOrgFile,
  bulkDeleteOrgDrive,
} from "../../api/orgKbService";
import { showApiError, showToast } from "../../utils/toast";

const VIEW_KEY = "orgDrive.view";

export default function OrgDrivePage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("org-kb.edit");
  const canDelete = hasPermission("org-kb.delete");

  const [folderId, setFolderId] = useState(null);
  const [fileSearch, setFileSearch] = useState("");
  const [debouncedFileSearch, setDebouncedFileSearch] = useState("");

  const [uploadOpen, setUploadOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);

  // Persist view choice across sessions so users don't get yanked back into
  // cards every page refresh.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) || "cards"; } catch { return "cards"; }
  });
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch {} }, [view]);

  const [selection, setSelection] = useState({ files: new Set(), folders: new Set() });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const clearSelection = () => setSelection({ files: new Set(), folders: new Set() });

  // Drop selection when navigation context changes so users can't act on
  // items they're no longer looking at.
  useEffect(() => { clearSelection(); }, [folderId, debouncedFileSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFileSearch(fileSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [fileSearch]);

  const drive = useApi(
    () => {
      const params = {};
      if (debouncedFileSearch) params.search = debouncedFileSearch;
      else if (folderId) params.folder_id = folderId;
      return listOrgFiles(params);
    },
    [folderId, debouncedFileSearch],
  );

  const driveData = drive.data?.data || {};
  const folders = Array.isArray(driveData.folders) ? driveData.folders : [];
  const files = Array.isArray(driveData.files) ? driveData.files : [];
  const breadcrumbs = Array.isArray(driveData.breadcrumbs) ? driveData.breadcrumbs : [];
  const currentFolder = driveData.folder;
  const isSearching = !!debouncedFileSearch;

  const handleUpload = async ({ files: picked, name }) => {
    if (!picked?.length) return;
    const fd = new FormData();
    for (const f of picked) fd.append("files[]", f);
    if (picked.length === 1 && name?.trim()) fd.append("name", name.trim());
    if (folderId) fd.append("folder_id", folderId);
    await uploadOrgFiles(fd);
    showToast("success", picked.length === 1 ? "File uploaded" : `${picked.length} files uploaded`);
    drive.reload();
  };

  const onCreateFolder = async (name) => {
    if (!name?.trim()) return;
    await createOrgFolder({ name: name.trim(), parent_id: folderId });
    showToast("success", "Folder created");
    drive.reload();
  };

  const onDeleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.original_name}"? This cannot be undone.`)) return;
    try {
      await deleteOrgFile(file.id);
      showToast("success", "File deleted");
      drive.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  const onDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"?`)) return;
    try {
      await deleteOrgFolder(folder.id);
      showToast("success", "Folder deleted");
      drive.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  const onDownload = async (file) => {
    try {
      await downloadOrgFile(file.id, file.original_name);
    } catch (err) {
      showApiError(err);
    }
  };

  const onBulkDelete = async () => {
    const fileIds = Array.from(selection.files);
    const folderIds = Array.from(selection.folders);
    if (!fileIds.length && !folderIds.length) return;
    const total = fileIds.length + folderIds.length;
    if (!window.confirm(`Delete ${total} selected item${total === 1 ? "" : "s"}? Non-empty folders will be skipped.`)) return;
    setBulkDeleting(true);
    try {
      const res = await bulkDeleteOrgDrive({ file_ids: fileIds, folder_ids: folderIds });
      showToast("success", res?.message || "Selection deleted");
      clearSelection();
      drive.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBulkDeleting(false);
    }
  };

  const onBulkDownload = async () => {
    // Folders can't be downloaded — act on selected files only.
    const picked = files.filter((f) => selection.files.has(f.id));
    if (!picked.length) return;
    setBulkDownloading(true);
    try {
      // Sequential so the browser doesn't choke on a burst of blob fetches.
      for (const f of picked) {
        await downloadOrgFile(f.id, f.original_name);
      }
      showToast("success", `Downloaded ${picked.length} file${picked.length === 1 ? "" : "s"}`);
    } catch (err) {
      showApiError(err);
    } finally {
      setBulkDownloading(false);
    }
  };

  const navigateTo = (folder) => {
    setFileSearch("");
    setFolderId(folder?.id || null);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Org Drive</h1>
          <div className="page-sub">Shared files and folders for the whole team</div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 12, gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          onNavigate={(id) => { setFileSearch(""); setFolderId(id); }}
        />
        <div className="row gap-sm" style={{ marginLeft: "auto" }}>
          {canEdit && (
            <button className="btn btn-secondary btn-sm" onClick={() => setNewFolderOpen(true)}>
              <Icon name="folder-plus" size={13} /> New folder
            </button>
          )}
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
              <Icon name="upload" size={13} /> Upload
            </button>
          )}
        </div>
      </div>

      <div className="row" style={{ marginBottom: 12, gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200, maxWidth: 360 }}>
          <Icon
            name="search"
            size={13}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
          />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search files & folders…"
            value={fileSearch}
            onChange={(e) => setFileSearch(e.target.value)}
          />
        </div>
        <div style={{ marginLeft: "auto" }}>
          <ViewToggle view={view} onChange={setView} />
        </div>
      </div>

      {isSearching && (
        <div className="text-xs t-muted" style={{ marginBottom: 8 }}>
          Showing search results across the entire drive.
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 8 }}
            onClick={() => setFileSearch("")}
          >
            Clear search
          </button>
        </div>
      )}

      <BulkActionBar
        count={selection.files.size + selection.folders.size}
        onClear={clearSelection}
        // Download applies to files only — offered when at least one file
        // is selected. Move needs edit rights; Delete needs delete rights.
        onDownload={selection.files.size > 0 ? onBulkDownload : undefined}
        onMove={canEdit ? () => setBulkMoveOpen(true) : undefined}
        onDelete={canDelete ? onBulkDelete : undefined}
        busy={bulkDeleting || bulkDownloading}
      />

      {drive.loading ? (
        <LoadingBlock rows={3} height={80} />
      ) : folders.length === 0 && files.length === 0 ? (
        <EmptyState
          icon="folder"
          title={isSearching ? "No matches" : currentFolder ? "Empty folder" : "No files or folders"}
          hint={isSearching ? "Try a different search." : canEdit ? "Create a folder or upload a file to start organising." : "Nothing here yet."}
        />
      ) : (
        <DriveExplorer
          folders={folders}
          files={files}
          view={view}
          isSearching={isSearching}
          canEdit={canEdit}
          canDelete={canDelete}
          // Selection is always on for the org drive — even view-only
          // staff can bulk-download. Move/Delete buttons in the bar
          // self-gate on canEdit / canDelete.
          selectable
          selection={selection}
          onSelectionChange={setSelection}
          onOpenFolder={navigateTo}
          onDownloadFile={onDownload}
          onRenameFile={(f) => setRenameTarget({ kind: "file", row: f })}
          onRenameFolder={(f) => setRenameTarget({ kind: "folder", row: f })}
          onMoveFile={(f) => setMoveTarget({ kind: "file", row: f })}
          onMoveFolder={(f) => setMoveTarget({ kind: "folder", row: f })}
          onDeleteFile={onDeleteFile}
          onDeleteFolder={onDeleteFolder}
        />
      )}

      <UploadFileDrawer
        open={uploadOpen}
        folderName={currentFolder?.name}
        onClose={() => setUploadOpen(false)}
        onSubmit={async (payload) => {
          try {
            await handleUpload(payload);
            setUploadOpen(false);
          } catch (err) {
            showApiError(err);
            throw err;
          }
        }}
      />

      <NewFolderDrawer
        open={newFolderOpen}
        parentName={currentFolder?.name}
        onClose={() => setNewFolderOpen(false)}
        onSubmit={async (name) => {
          try {
            await onCreateFolder(name);
            setNewFolderOpen(false);
          } catch (err) {
            showApiError(err);
            throw err;
          }
        }}
      />

      <RenameDrawer
        target={renameTarget}
        onClose={() => setRenameTarget(null)}
        onSaved={() => { setRenameTarget(null); drive.reload(); }}
      />

      <MoveDrawer
        target={moveTarget}
        onClose={() => setMoveTarget(null)}
        onSaved={() => { setMoveTarget(null); drive.reload(); }}
      />

      <BulkMoveDrawer
        open={bulkMoveOpen}
        fileIds={Array.from(selection.files)}
        folderIds={Array.from(selection.folders)}
        onClose={() => setBulkMoveOpen(false)}
        onSaved={() => {
          setBulkMoveOpen(false);
          clearSelection();
          drive.reload();
        }}
      />
    </>
  );
}

function Breadcrumbs({ breadcrumbs, onNavigate }) {
  return (
    <div className="row" style={{ gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => onNavigate(null)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px" }}
        title="Drive root"
      >
        <Icon name="home" size={13} />
        <span className="fw-600">Drive</span>
      </button>
      {breadcrumbs.map((b, i) => {
        const last = i === breadcrumbs.length - 1;
        return (
          <span key={b.id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon name="chevron-right" size={12} style={{ color: "var(--ink-400)" }} />
            {last ? (
              <span className="fw-600" style={{ fontSize: 13 }}>{b.name}</span>
            ) : (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => onNavigate(b.id)}
                style={{ padding: "4px 8px" }}
              >
                {b.name}
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}

function NewFolderDrawer({ open, parentName, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) { setName(""); setSaving(false); } }, [open]);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try { await onSubmit(name); } catch {} finally { setSaving(false); }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New folder"
      subtitle={`In ${parentName ? `“${parentName}”` : "drive root"}`}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!name.trim() || saving}>
            <Icon name="folder-plus" size={13} /> {saving ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Folder name</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="e.g. Templates"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <div className="field-hint">Slashes are not allowed.</div>
        </div>
      </div>
    </Drawer>
  );
}

function RenameDrawer({ target, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const open = !!target;
  const isFolder = target?.kind === "folder";

  useEffect(() => {
    if (!target) { setName(""); setSaving(false); return; }
    if (isFolder) {
      setName(target.row.name || "");
    } else {
      const base = (target.row.original_name || "").replace(/\.[A-Za-z0-9]{1,8}$/, "");
      setName(base);
    }
  }, [target, isFolder]);

  const submit = async () => {
    if (!name.trim() || !target) return;
    setSaving(true);
    try {
      if (isFolder) {
        await updateOrgFolder(target.row.id, { name: name.trim() });
      } else {
        await renameOrgFile(target.row.id, name.trim());
      }
      showToast("success", isFolder ? "Folder renamed" : "File renamed");
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const ext = !isFolder ? (target?.row.original_name?.match(/\.[A-Za-z0-9]{1,8}$/)?.[0] || "") : "";
  const preview = !isFolder && name.trim()
    ? `${name.trim().replace(/\.[A-Za-z0-9]{1,8}$/, "")}${ext}`
    : target?.row.original_name || target?.row.name || "—";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isFolder ? "Rename folder" : "Rename file"}
      subtitle={isFolder ? target?.row.name : target?.row.original_name}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!name.trim() || saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">New name</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          {!isFolder && (
            <div className="field-hint">
              Saved as: <span className="t-mono">{preview}</span>
              {ext ? " — extension is preserved automatically." : ""}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}

function MoveDrawer({ target, onClose, onSaved }) {
  const open = !!target;
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [destinationId, setDestinationId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // Walk every folder so the user can move the row anywhere in the
        // tree. Tiny org drives — a BFS is fine; no need for a dedicated
        // tree endpoint.
        const collected = [];
        const queue = [null];
        const seen = new Set();
        while (queue.length) {
          const parent = queue.shift();
          if (parent !== null && seen.has(parent)) continue;
          if (parent !== null) seen.add(parent);
          const res = await listOrgFiles(parent ? { folder_id: parent } : {});
          const folders = res.data?.folders || [];
          for (const f of folders) {
            collected.push({ id: f.id, name: f.name, parent_id: f.parent_id });
            queue.push(f.id);
          }
        }
        if (!alive) return;
        setTree(collected);
        setDestinationId("");
      } catch (err) {
        showApiError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const options = useMemo(() => {
    const byId = new Map(tree.map((f) => [f.id, f]));
    const labelOf = (id) => {
      const chain = [];
      let cur = byId.get(id);
      let safety = 0;
      while (cur && safety++ < 100) {
        chain.unshift(cur.name);
        cur = cur.parent_id ? byId.get(cur.parent_id) : null;
      }
      return chain.join(" / ");
    };

    let blocked = new Set();
    if (target?.kind === "folder") {
      blocked.add(target.row.id);
      const stack = [target.row.id];
      while (stack.length) {
        const id = stack.pop();
        for (const f of tree) {
          if (f.parent_id === id && !blocked.has(f.id)) {
            blocked.add(f.id);
            stack.push(f.id);
          }
        }
      }
    }

    const list = [{ id: "", label: "— Drive root —" }];
    for (const f of tree) {
      if (blocked.has(f.id)) continue;
      list.push({ id: String(f.id), label: labelOf(f.id) });
    }
    return list;
  }, [tree, target]);

  const submit = async () => {
    if (!target) return;
    const newId = destinationId === "" ? null : Number(destinationId);

    if (target.kind === "file" && (target.row.folder_id || null) === newId) {
      onClose?.();
      return;
    }
    if (target.kind === "folder" && (target.row.parent_id || null) === newId) {
      onClose?.();
      return;
    }

    setSaving(true);
    try {
      if (target.kind === "folder") {
        await updateOrgFolder(target.row.id, { parent_id: newId });
      } else {
        await updateOrgFile(target.row.id, { folder_id: newId });
      }
      showToast("success", "Moved");
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Move ${target?.kind === "folder" ? "folder" : "file"}`}
      subtitle={target?.row?.name || target?.row?.original_name}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || loading}>
            {saving ? "Moving…" : "Move here"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Destination</label>
          {loading ? (
            <div className="t-muted text-sm">Loading folders…</div>
          ) : (
            <SearchableSelect
              value={destinationId}
              onChange={(v) => setDestinationId(v ?? "")}
              options={options}
              placeholder="Select destination…"
              searchPlaceholder="Search folders…"
              width="100%"
            />
          )}
          <div className="field-hint">
            {target?.kind === "folder"
              ? "A folder can't be moved into itself or one of its subfolders."
              : "Pick any folder, or move to the drive root."}
          </div>
        </div>
      </div>
    </Drawer>
  );
}

// Moves a whole selection (files + folders) into one destination folder.
// Mirrors MoveDrawer's folder-tree picker, but blocks every selected
// folder and its descendants so a folder can't be moved into itself.
function BulkMoveDrawer({ open, fileIds = [], folderIds = [], onClose, onSaved }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [destinationId, setDestinationId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const collected = [];
        const queue = [null];
        const seen = new Set();
        while (queue.length) {
          const parent = queue.shift();
          if (parent !== null && seen.has(parent)) continue;
          if (parent !== null) seen.add(parent);
          const res = await listOrgFiles(parent ? { folder_id: parent } : {});
          const fld = res.data?.folders || [];
          for (const f of fld) {
            collected.push({ id: f.id, name: f.name, parent_id: f.parent_id });
            queue.push(f.id);
          }
        }
        if (!alive) return;
        setTree(collected);
        setDestinationId("");
      } catch (err) {
        showApiError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const options = useMemo(() => {
    const byId = new Map(tree.map((f) => [f.id, f]));
    const labelOf = (id) => {
      const chain = [];
      let cur = byId.get(id);
      let safety = 0;
      while (cur && safety++ < 100) {
        chain.unshift(cur.name);
        cur = cur.parent_id ? byId.get(cur.parent_id) : null;
      }
      return chain.join(" / ");
    };

    // Block every selected folder + its whole subtree.
    const blocked = new Set(folderIds);
    const stack = [...folderIds];
    while (stack.length) {
      const id = stack.pop();
      for (const f of tree) {
        if (f.parent_id === id && !blocked.has(f.id)) {
          blocked.add(f.id);
          stack.push(f.id);
        }
      }
    }

    const list = [{ id: "", label: "— Drive root —" }];
    for (const f of tree) {
      if (blocked.has(f.id)) continue;
      list.push({ id: String(f.id), label: labelOf(f.id) });
    }
    return list;
  }, [tree, folderIds]);

  const total = fileIds.length + folderIds.length;

  const submit = async () => {
    const newId = destinationId === "" ? null : Number(destinationId);
    setSaving(true);
    try {
      for (const id of folderIds) {
        await updateOrgFolder(id, { parent_id: newId });
      }
      for (const id of fileIds) {
        await updateOrgFile(id, { folder_id: newId });
      }
      showToast("success", `Moved ${total} item${total === 1 ? "" : "s"}`);
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Move ${total} item${total === 1 ? "" : "s"}`}
      subtitle={`${folderIds.length} folder${folderIds.length === 1 ? "" : "s"} · ${fileIds.length} file${fileIds.length === 1 ? "" : "s"}`}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || loading || total === 0}>
            {saving ? "Moving…" : "Move here"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Destination</label>
          {loading ? (
            <div className="t-muted text-sm">Loading folders…</div>
          ) : (
            <SearchableSelect
              value={destinationId}
              onChange={(v) => setDestinationId(v ?? "")}
              options={options}
              placeholder="Select destination…"
              searchPlaceholder="Search folders…"
              width="100%"
            />
          )}
          <div className="field-hint">
            All selected items move into the chosen folder (or the drive root).
            Selected folders and their subfolders are excluded from the list.
          </div>
        </div>
      </div>
    </Drawer>
  );
}

function UploadFileDrawer({ open, folderName, onClose, onSubmit }) {
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setName("");
      setSaving(false);
    }
  }, [open]);

  const submit = async () => {
    if (!files.length) return;
    setSaving(true);
    try {
      await onSubmit({ files, name });
    } catch {
      /* toast already shown by parent */
    } finally {
      setSaving(false);
    }
  };

  const singleFile = files.length === 1 ? files[0] : null;
  const ext = singleFile?.name?.includes(".") ? singleFile.name.split(".").pop() : "";
  const previewName = name.trim()
    ? `${name.trim().replace(/\.[A-Za-z0-9]{1,8}$/, "")}${ext ? "." + ext : ""}`
    : singleFile?.name || "—";
  const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);
  // Soft warning threshold — see BusinessDrivePanel for rationale.
  const SIZE_WARN_BYTES = 100 * 1024 * 1024;
  const SIZE_HARD_LIMIT_BYTES = 500 * 1024 * 1024;
  const sizeWarning = totalBytes > SIZE_WARN_BYTES && totalBytes <= SIZE_HARD_LIMIT_BYTES;
  const sizeBlocked = totalBytes > SIZE_HARD_LIMIT_BYTES;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={files.length > 1 ? "Upload files" : "Upload file"}
      subtitle={`Into ${folderName ? `“${folderName}”` : "drive root"}`}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!files.length || saving || sizeBlocked}>
            <Icon name="upload" size={13} /> {saving ? "Uploading…" : files.length > 1 ? `Upload ${files.length}` : "Upload"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Files</label>
          <input
            type="file"
            multiple
            className="input"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length > 0 && (
            <div className="t-muted text-xs" style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
              {files.length === 1 ? (
                <span>{files[0].name} · {formatBytes(files[0].size)}</span>
              ) : (
                <>
                  <span>{files.length} files selected · {formatBytes(totalBytes)} total</span>
                  {files.slice(0, 5).map((f, i) => (
                    <span key={i} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      • {f.name} <span className="t-muted">({formatBytes(f.size)})</span>
                    </span>
                  ))}
                  {files.length > 5 && <span>…and {files.length - 5} more</span>}
                </>
              )}
            </div>
          )}
          {sizeWarning && (
            <div className="field-hint" style={{ color: "var(--warning)", marginTop: 6 }}>
              <Icon name="alert-triangle" size={12} /> Total is {formatBytes(totalBytes)}. Many servers cap uploads at ~100 MB —
              this may fail. If it does, ask your admin to raise <code>post_max_size</code> on the server.
            </div>
          )}
          {sizeBlocked && (
            <div className="field-hint" style={{ color: "var(--danger)", marginTop: 6 }}>
              <Icon name="alert-triangle" size={12} /> Total is {formatBytes(totalBytes)} — too large for a single batch.
              Split the upload into smaller groups (≤ 500 MB).
            </div>
          )}
          <div className="field-hint">You can select multiple files at once.</div>
        </div>
        {files.length <= 1 && (
          <div className="field">
            <label className="field-label">
              Name <span className="t-muted text-xs">(optional)</span>
            </label>
            <input
              className="input"
              placeholder="Optional display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="field-hint">
              Saved as: <span className="t-mono">{previewName}</span>
              {ext ? " — extension is taken from the uploaded file." : ""}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}

